import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import SlidingModal from "../../SlidingModal/slidingModal";
import {
  StyledInput, StyledInputContainer, StyledInputGroup, StyledLabel,
} from "../../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { createHiringCandidateFromParse } from "../../../api/services/hroneApi";
import {
  buildCreateCandidatePayload,
  getAtsMatchBadgeClass,
  getAtsScoreColor,
  isPdfResumeUrl,
  normalizeApplication,
  toEntryListString,
} from "../../../util/hiringUtils";
import "./index.css";

const ResumeReviewModal = ({ isOpen, parsedData, jobId, onSuccess, onClose }) => {
  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const [form, setForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!parsedData) return;
    setForm({
      name:              parsedData.name              || "",
      email:             parsedData.email             || "",
      phone:             parsedData.phone             || "",
      skills:            parsedData.skills            || "",
      experience:        parsedData.experience        || "",
      education:         toEntryListString(parsedData.education)         || "",
      previousCompanies: toEntryListString(parsedData.previousCompanies) || "",
      certifications:    toEntryListString(parsedData.certifications)    || "",
      projects:          toEntryListString(parsedData.projects)          || "",
      designation:       parsedData.designation       || "",
    });
  }, [parsedData]);

  if (!parsedData || !form) return null;

  const previewUrl = parsedData.localPreviewUrl || parsedData.resumeUrl || "";
  const canPreviewResume = isPdfResumeUrl(
    previewUrl,
    parsedData.resumeFileName || parsedData.resumeFile?.name || "",
  );
  const atsColor     = getAtsScoreColor(parsedData.atsScore);
  const matchLabel   = parsedData.atsMatchLabel;
  const badgeClass   = getAtsMatchBadgeClass(matchLabel);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (isSaving) return;
    if (!form.name?.trim() || !form.email?.trim()) {
      toast.error("Name and email are required");
      return;
    }

    setIsSaving(true);
    try {
      const candidatePayload = buildCreateCandidatePayload(form, {
        parseId: parsedData.parseId,
        tempId:  parsedData.tempId,
      });
      const res  = await createHiringCandidateFromParse(jobId, candidatePayload);
      const data = await res.json();
      if (!data.status) throw new Error(data.message || data.detail || "Failed to save candidate");
      onSuccess(normalizeApplication(data.data));
      toast.success("Candidate saved successfully");
    } catch (err) {
      if (!navigator.onLine) {
        toast.error("Network error. Your edits are preserved — please try again.");
      } else {
        toast.error(err.message || "Failed to save candidate");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const Field = ({ label, fieldKey, full = false }) => (
    <StyledInputGroup className={full ? "resume-review-field-full" : ""}>
      <StyledInputContainer>
        <StyledInput
          type="text"
          placeholder=" "
          value={form[fieldKey] || ""}
          onChange={(e) => setField(fieldKey, e.target.value)}
          style={{ width: "100%" }}
        />
        <StyledLabel className="user-label">{label}</StyledLabel>
      </StyledInputContainer>
    </StyledInputGroup>
  );

  return (
    <SlidingModal
      isModalOpen={isOpen}
      modalName="Review Parsed Resume"
      closeBtn={isSaving ? () => {} : onClose}
    >
      <div className="resume-review-modal" style={{ "--hiring-primary": primaryColor }}>
        <div className="resume-review-ats-banner">
          <div>
            <p className="resume-review-ats-label">ATS Score</p>
            <p className="resume-review-ats-score" style={{ color: atsColor }}>
              {parsedData.atsScore}%
            </p>
          </div>
          <span className={badgeClass}>{matchLabel}</span>
        </div>

        <div className="resume-review-body">
          <div className="resume-review-fields">
            <p className="resume-review-section-title">Extracted Details</p>
            <div className="resume-review-grid">
              <Field label="Name" fieldKey="name" />
              <Field label="Email" fieldKey="email" />
              <Field label="Phone" fieldKey="phone" />
              <Field label="Designation" fieldKey="designation" />
              <Field label="Skills" fieldKey="skills" full />
              <Field label="Experience" fieldKey="experience" />
              <Field label="Education" fieldKey="education" />
              <Field label="Previous Companies" fieldKey="previousCompanies" full />
              <Field label="Certifications" fieldKey="certifications" full />
              <Field label="Projects" fieldKey="projects" full />
            </div>
          </div>

          <div className="resume-review-preview">
            <p className="resume-review-section-title">Resume Preview</p>
            {previewUrl && canPreviewResume ? (
              <iframe
                src={`${previewUrl}#navpanes=0`}
                title="Resume Preview"
                className="resume-review-iframe"
              />
            ) : (
              <div className="resume-review-preview-empty">
                {previewUrl
                  ? "Only PDF files can be viewed here."
                  : "Preview not available for this file type."}
              </div>
            )}
          </div>
        </div>

        <div className="resume-review-actions">
          <SecondaryButton type="button" onClick={onClose} disabled={isSaving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving…" : "Save Candidate"}
          </PrimaryButton>
        </div>
      </div>
    </SlidingModal>
  );
};

export default ResumeReviewModal;
